import React from 'react';
import { X, Users } from 'lucide-react';

/**
 * GroupMembersModal - Modal showing group chat members
 */
const GroupMembersModal = ({
  isOpen,
  onClose,
  groupName,
  members = [],
  className = ''
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
      <div className={`bg-black border border-gray-400 rounded-xl w-full max-w-md mx-4 overflow-hidden hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 ${className}`}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-400/20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="text-purple-400" size={20} />
            <div>
              <h3 className="text-lg font-bold text-white">Group Members</h3>
              <p className="text-xs text-gray-400">{groupName || 'Group Chat'}</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            data-testid="group-members-close"
            className="p-2 hover:bg-gray-900 rounded-full transition-colors"
          >
            <X size={20} className="text-gray-400" />
          </button>
        </div>

        {/* Members List */}
        <div className="max-h-[400px] overflow-y-auto p-4">
          {members.length > 0 ? (
            <div className="space-y-3">
              {members.map((member, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center gap-3 p-3 bg-gray-800/50 rounded-lg"
                >
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0">
                    {member.avatar ? (
                      <img 
                        src={member.avatar} 
                        alt={member.name} 
                        className="w-full h-full rounded-full object-cover" 
                      />
                    ) : (
                      <span className="text-white font-medium">
                        {(member.name || member.phone || '?')[0].toUpperCase()}
                      </span>
                    )}
                  </div>

                  {/* Member Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">
                      {member.name || member.phone || 'Unknown'}
                    </p>
                    {member.phone && member.name && (
                      <p className="text-xs text-gray-400 truncate">{member.phone}</p>
                    )}
                    {member.role && (
                      <p className="text-xs text-purple-400">{member.role}</p>
                    )}
                  </div>

                  {/* Online Status */}
                  <div className="flex-shrink-0">
                    <span className={`text-xs font-medium ${
                      member.isOnline ? 'text-green-400' : 'text-gray-500'
                    }`}>
                      {member.isOnline ? 'Online' : 'Offline'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              No member data available for this group
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-400/20">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default GroupMembersModal;
